import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { ControleDetails } from 'src/app/data/declaration/models/controle-details.model';
import { SyntheseDeclarationControle } from 'src/app/data/declaration/models/synthese-declaration-controle.model';
import { ControlesService } from 'src/app/data/declaration/services/controles/controles.service';
import { SyntheseDeclarationControleService } from 'src/app/data/declaration/services/synthese-declaration-controle/synthese-declaration-controle.service';
import { Operateur } from 'src/app/data/intervenant/models/operateur.model';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { LoaderService } from '../../../../../core/services/loader/loader.service';
import { EChartsOption } from 'echarts';

@Component({
    selector: 'app-detail-controle',
    templateUrl: './detail-controle.component.html',
    styleUrls: ['./detail-controle.component.scss'],
    standalone: false
})
export class DetailControleComponent implements OnInit {
    @Input() public idOperateur: number;
    @Input() public annee: string;
    @Input() public isMillesime: boolean;

    public controles: ControleDetails[];
    public syntheses: SyntheseDeclarationControle[];
    public operateur: Operateur;
    public cvi: string;

syntheseChartOption: EChartsOption = {};

    synthesesParAnnee: any[] = [];

    syntheseResume3Ans = {
    nbLot: 0,
    volumeDeclare: 0,
    nbControle: 0,
    volumeControle: 0
    };

    constructor(
        private readonly controlesService: ControlesService,
        private readonly syntheseDeclarationControleService: SyntheseDeclarationControleService,
        private readonly operateursService: OperateursService,
        private readonly loaderService: LoaderService,
        public readonly modal: NgbActiveModal
    ) {}

    ngOnInit() {
        this.loadData();
    }

    private loadData(): void {
        // Charger les contrôles
        forkJoin([
            this.controlesService.getControlesDetails(this.idOperateur, this.annee),
            this.syntheseDeclarationControleService.getSynthese(this.idOperateur, this.annee),
            this.operateursService.getOperateur(this.idOperateur),
            this.operateursService.getInformationsDomaine(this.idOperateur)
        ]).subscribe(([controles, synthese, operateur, info]) => {
            [this.controles, this.syntheses, this.operateur] = [controles, synthese, operateur];
            this.cvi = info.find(i => i.code === 'VIN_CVI').valeur;


            this.construireGraphiqueSynthese();
        });
    }

    /** Ferme la fenête */
    public onClose(): void {
        this.modal.dismiss();
    }

    private construireGraphiqueSynthese(): void {
  const syntheses = this.syntheses ?? [];

  const formatNombre = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  const groupes = syntheses.reduce((acc, s) => {
    const annee = String(s.annee ?? 'Non renseignée');

    if (!acc[annee]) {
      acc[annee] = {
        annee,
        nbLot: 0,
        volumeDeclare: 0,
        nbControle: 0,
        volumeControle: 0,
        details: []
      };
    }

    acc[annee].nbLot += s.nbLot ?? 0;
    acc[annee].volumeDeclare += s.volumeDeclare ?? 0;
    acc[annee].nbControle += s.nbControle ?? 0;
    acc[annee].volumeControle += s.volumeControle ?? 0;

    acc[annee].details.push({
      libelleCDC: s.libelleCDC,
      typeDeclaration: s.typeDeclaration,
      nbLot: s.nbLot ?? 0,
      volumeDeclare: s.volumeDeclare ?? 0,
      nbControle: s.nbControle ?? 0,
      volumeControle: s.volumeControle ?? 0
    });

    return acc;
  }, {} as Record<string, any>);

  const donneesParAnnee = Object.values(groupes)
    .sort((a: any, b: any) => Number(a.annee) - Number(b.annee));

  const annees = donneesParAnnee.map((x: any) => x.annee);

  const volumesDeclares = donneesParAnnee.map((x: any) =>
    Number(x.volumeDeclare.toFixed(2))
  );

  const volumesControles = donneesParAnnee.map((x: any) =>
    Number(x.volumeControle.toFixed(2))
  );

  const nbLots = donneesParAnnee.map((x: any) => x.nbLot);
  const nbControles = donneesParAnnee.map((x: any) => x.nbControle);

  const detailsParAnnee = donneesParAnnee.reduce((acc: any, x: any) => {
    acc[x.annee] = x.details;
    return acc;
  }, {});

  const resumeParAnnee = donneesParAnnee.reduce((acc: any, x: any) => {
    acc[x.annee] = x;
    return acc;
  }, {});

  this.syntheseChartOption = {
    backgroundColor: '#ffffff',

    title: [
      {
        text: 'Volume',
        subtext: 'Déclaré / contrôlé',
        left: '23%',
        top: 8,
        textAlign: 'center',
        textStyle: {
          color: '#0f172a',
          fontSize: 14,
          fontWeight: 700
        },
        subtextStyle: {
          color: '#64748b',
          fontSize: 11
        }
      },
      {
        text: 'Nombre',
        subtext: 'Déclaré / contrôlé',
        left: '74%',
        top: 8,
        textAlign: 'center',
        textStyle: {
          color: '#0f172a',
          fontSize: 14,
          fontWeight: 700
        },
        subtextStyle: {
          color: '#64748b',
          fontSize: 11
        }
      }
    ],

    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: {
        type: 'shadow',
        link: [
          {
            xAxisIndex: [0, 1]
          }
        ]
      },
      formatter: (params: any) => {
        const annee = params?.[0]?.axisValue;
        const resume = resumeParAnnee[annee];
        const details = detailsParAnnee[annee] ?? [];

        if (!resume) {
          return '';
        }

        const detailsHtml = details
          .map((d: any) => {
            return `
              <div style="
                margin-top:8px;
                padding-top:8px;
                border-top:1px solid #e5e7eb;
              ">
                <div style="font-weight:600;color:#1e293b;">
                  ${d.libelleCDC ?? 'Cahier des charges non renseigné'}
                </div>

                <div style="font-size:12px;color:#64748b;margin-bottom:4px;">
                  ${d.typeDeclaration ?? 'Type non renseigné'}
                </div>

                <div style="font-size:12px;color:#334155;">
                  Lots déclarés : <strong>${d.nbLot}</strong> ·
                  Lots contrôlés : <strong>${d.nbControle}</strong><br/>
                  Volume déclaré : <strong>${formatNombre.format(d.volumeDeclare)}</strong> ·
                  Volume contrôlé : <strong>${formatNombre.format(d.volumeControle)}</strong>
                </div>
              </div>
            `;
          })
          .join('');

        return `
          <div style="min-width:300px;max-width:560px;">
            <div style="font-weight:700;font-size:14px;margin-bottom:8px;color:#0f172a;">
              ${ this.isMillesime?'Millésime':'Année' } ${annee}
            </div>

            <div style="
              display:grid;
              grid-template-columns:1fr 1fr;
              gap:8px;
              margin-bottom:10px;
            ">
              <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:6px;">
                <div style="font-size:11px;color:#64748b;">Volume déclaré</div>
                <strong>${formatNombre.format(resume.volumeDeclare)}</strong>
              </div>

              <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:6px;">
                <div style="font-size:11px;color:#64748b;">Volume contrôlé</div>
                <strong>${formatNombre.format(resume.volumeControle)}</strong>
              </div>

              <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:6px;">
                <div style="font-size:11px;color:#64748b;">Lots déclarés</div>
                <strong>${resume.nbLot}</strong>
              </div>

              <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:6px;">
                <div style="font-size:11px;color:#64748b;">Lots contrôlés</div>
                <strong>${resume.nbControle}</strong>
              </div>
            </div>

            <div style="font-weight:600;color:#0f172a;margin-top:6px;">
              Détail par cahier des charges
            </div>

            ${detailsHtml}
          </div>
        `;
      }
    },

    legend: {
      type: 'scroll',
      top: 52,
      left: 'center',
      textStyle: {
        color: '#334155'
      }
    },

    grid: [
      {
        left: '5%',
        right: '55%',
        top: 105,
        bottom: 40,
        containLabel: true
      },
      {
        left: '58%',
        right: '5%',
        top: 105,
        bottom: 40,
        containLabel: true
      }
    ],

    xAxis: [
      {
        type: 'category',
        gridIndex: 0,
        data: annees,
        axisLabel: {
          color: '#475569',
          fontWeight: 600
        },
        axisLine: {
          lineStyle: {
            color: '#cbd5e1'
          }
        }
      },
      {
        type: 'category',
        gridIndex: 1,
        data: annees,
        axisLabel: {
          color: '#475569',
          fontWeight: 600
        },
        axisLine: {
          lineStyle: {
            color: '#cbd5e1'
          }
        }
      }
    ],

    yAxis: [
      {
        type: 'value',
        gridIndex: 0,
        name: 'Volumes',
        axisLabel: {
          color: '#475569'
        },
        nameTextStyle: {
          color: '#64748b'
        },
        splitLine: {
          lineStyle: {
            color: '#e5e7eb'
          }
        }
      },
      {
        type: 'value',
        gridIndex: 1,
        name: 'Nombre',
        minInterval: 1,
        axisLabel: {
          color: '#475569'
        },
        nameTextStyle: {
          color: '#64748b'
        },
        splitLine: {
          lineStyle: {
            color: '#e5e7eb'
          }
        }
      }
    ],

    series: [
      {
        name: 'Volume déclaré',
        type: 'bar',
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: volumesDeclares,
        barMaxWidth: 34,
        itemStyle: {
          color: '#93c5fd',
          borderRadius: [6, 6, 0, 0]
        }
      },
      {
        name: 'Volume contrôlé',
        type: 'bar',
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: volumesControles,
        barMaxWidth: 34,
        itemStyle: {
          color: '#2563eb',
          borderRadius: [6, 6, 0, 0]
        }
      },
      {
        name: 'Lots déclarés',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: nbLots,
        barMaxWidth: 34,
        itemStyle: {
          color: '#fbbf24',
          borderRadius: [6, 6, 0, 0]
        }
      },
      {
        name: 'Lots contrôlés',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: nbControles,
        barMaxWidth: 34,
        itemStyle: {
          color: '#22c55e',
          borderRadius: [6, 6, 0, 0]
        }
      }
    ]
  };
}

}
